// Rango de un día natural en Europe/Madrid expresado en UTC (spec §5).
// Puro, sin librerías de fechas: Intl + Date nativos. Correcto en los cambios
// CET/CEST porque parte del offset real de la zona para ese instante.

const TZ = 'Europe/Madrid';

// `Date.UTC(50, ...)` NO es el año 50: la especificación mapea los años 0-99 a
// 1900+y por compatibilidad con el Javascript de 1995. Y en un `<input
// type="date">` teclear "50" en el campo del año produce exactamente `0050-…`,
// así que no es un caso de laboratorio. Sin esto, un rango del año 0050 se
// consultaba en 1950 sin decir nada: cuadro vacío y ningún error. Un año menor
// de 100 no es un dato válido del negocio, pero el fallo silencioso sí es
// problema nuestro.
// El desplazamiento es de 400 años, no de 1900: el calendario gregoriano se
// repite exacto cada 400 (es su ciclo de bisiestos), así que hacer la aritmética
// en el año y+400 y restar 400 al final da el mismo día del mes, incluido el
// desbordamiento de febrero que `sumarDias` provoca al restar días.
function utcDia(y: number, mes0: number, d: number, h = 0, mi = 0, s = 0): number {
  if (y < 0 || y > 99) return Date.UTC(y, mes0, d, h, mi, s);
  const fecha = new Date(Date.UTC(y + 400, mes0, d, h, mi, s));
  fecha.setUTCFullYear(fecha.getUTCFullYear() - 400);
  return fecha.getTime();
}

function partesDia(dia: string): [number, number, number] {
  const [y, m, d] = dia.split('-').map(Number);
  return [y ?? 0, (m ?? 1) - 1, d ?? 1];
}

const DTF = new Intl.DateTimeFormat('en-US', {
  timeZone: TZ,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});

// Offset (ms) de Europe/Madrid respecto a UTC en un instante dado.
function offsetMs(instante: number): number {
  const p: Record<string, number> = {};
  for (const { type, value } of DTF.formatToParts(new Date(instante))) {
    if (type !== 'literal') p[type] = Number(value);
  }
  // `utcDia` y no `Date.UTC`: aquí el año sale de `formatToParts`, así que un
  // instante del año 50 volvía a caer en el mapeo 1900+y y el offset resultante
  // era de diecinueve siglos. El síntoma era una fecha ISO de año expandido
  // (`-003651-…`) viajando a PostgREST.
  const comoUtc = utcDia(
    p.year ?? 0,
    (p.month ?? 1) - 1,
    p.day ?? 1,
    (p.hour ?? 0) % 24,
    p.minute ?? 0,
    p.second ?? 0
  );
  return comoUtc - instante;
}

// Instante UTC de las 00:00 de `dia` (YYYY-MM-DD) en Europe/Madrid.
// Doble pasada: la primera estima con el offset de la medianoche UTC, la
// segunda corrige si el offset cambió (transición DST).
function medianocheMadrid(dia: string): number {
  const [y, m, d] = partesDia(dia);
  const utcGuess = utcDia(y, m, d);
  let instante = utcGuess - offsetMs(utcGuess);
  instante = utcGuess - offsetMs(instante);
  return instante;
}

// Construir un `Intl.DateTimeFormat` es caro y `diaMadrid` se llama una vez por
// llamada al agregar la serie. Medido por el revisor adversarial: 2.000 llamadas
// cuestan 50,7 ms creando el formateador cada vez y 1,2 ms reutilizándolo. Con
// rangos de años eso deja de ser una curiosidad (AD-P3-4).
const DIA_ISO = new Intl.DateTimeFormat('en-CA', { timeZone: TZ });

// Día de hoy (YYYY-MM-DD) en Europe/Madrid, independiente de la TZ local.
export function hoyMadrid(): string {
  return DIA_ISO.format(new Date());
}

// Día natural (YYYY-MM-DD) de un instante ISO en Europe/Madrid.
export function diaMadrid(iso: string): string {
  return DIA_ISO.format(new Date(iso));
}

// --- Periodos del cuadro de mando (spec cuadro-mando §4) ---

export type Periodo = 'hoy' | '7d' | '30d';

export interface RangoPeriodo {
  desde: string; // ISO UTC inclusive: 00:00 Europe/Madrid del primer día
  hasta: string; // ISO UTC exclusive: 24:00 Europe/Madrid del último día
  dias: string[]; // días naturales YYYY-MM-DD, orden ascendente
}

const DIAS_POR_PERIODO: Record<Periodo, number> = { hoy: 1, '7d': 7, '30d': 30 };

function sumarDias(dia: string, n: number): string {
  const [y, m, d] = partesDia(dia);
  return new Date(utcDia(y, m, d + n)).toISOString().slice(0, 10);
}

function rangoDeDias(ultimo: string, n: number): RangoPeriodo {
  const dias = Array.from({ length: n }, (_, i) => sumarDias(ultimo, i - (n - 1)));
  const primero = dias[0] ?? ultimo;
  return {
    desde: _rangoDia(primero).desde,
    hasta: _rangoDia(ultimo).hasta,
    dias,
  };
}

// Rango de días explícito del cuadro (change 002). Los campos se llaman
// `diaDesde`/`diaHasta` y no `desde`/`hasta` A PROPÓSITO: en `RangoPeriodo`,
// `desde`/`hasta` son instantes ISO UTC, y aquí son días naturales YYYY-MM-DD.
// Confundirlos sería un error de un día que no daría ningún síntoma.
export interface RangoDias {
  diaDesde: string;
  diaHasta: string;
}

// Lo que el cuadro guarda como selección: un preset o un rango explícito.
// Se discrimina en ejecución con `typeof s === 'string'`.
export type SeleccionPeriodo = Periodo | RangoDias;

// Días naturales entre dos fechas, contando ambos extremos. Aritmética UTC sobre
// las tres cifras del día, igual que `sumarDias`: los días del calendario no
// tienen cambio de hora — el DST solo aparece al convertirlos en instantes, y de
// eso ya se ocupa `_rangoDia`.
export function _diasEntre(diaDesde: string, diaHasta: string): number {
  const aUtc = (d: string): number => utcDia(...partesDia(d));
  return Math.round((aUtc(diaHasta) - aUtc(diaDesde)) / 86_400_000) + 1;
}

// Un día es válido si además de tener el formato existe en el calendario:
// `2026-02-30` pasa el regex y `Date.UTC` lo desborda a marzo sin protestar, así
// que el cuadro consultaría un día distinto del pedido sin decirlo. Se comprueba
// por ida y vuelta, que es la única forma barata de detectarlo.
export function _diaValido(dia: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dia)) return false;
  const t = utcDia(...partesDia(dia));
  return Number.isFinite(t) && new Date(t).toISOString().slice(0, 10) === dia;
}

// Periodo actual + el inmediatamente anterior de igual longitud (para el delta
// del KPI). Límites con _rangoDia (correcto en CET/CEST).
//
// Acepta un preset o un rango explícito. Ampliar el parámetro de entrada es
// compatible hacia atrás por construcción: quien pasaba un string sigue
// compilando y comportándose igual.
export function resolverPeriodo(
  seleccion: SeleccionPeriodo,
  hoy: string = hoyMadrid()
): { actual: RangoPeriodo; previo: RangoPeriodo } {
  if (typeof seleccion === 'string') {
    const n = DIAS_POR_PERIODO[seleccion];
    return {
      actual: rangoDeDias(hoy, n),
      previo: rangoDeDias(sumarDias(hoy, -n), n),
    };
  }

  const { diaDesde, diaHasta } = seleccion;
  // Se valida aquí y se lanza, como `_rangoDia`: un rango inválido es un error
  // de programación, no un estado de la interfaz. El selector no deja emitirlo.
  for (const d of [diaDesde, diaHasta]) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) {
      throw new Error(`resolverPeriodo: día inválido "${d}" (esperado YYYY-MM-DD)`);
    }
  }
  if (diaDesde > diaHasta) {
    throw new Error(`resolverPeriodo: rango invertido (${diaDesde} > ${diaHasta})`);
  }

  // `rangoDeDias(ultimo, n)` ya es el motor que hace falta: no hay aritmética
  // nueva, solo calcular la n. Por eso un rango de un día sale idéntico al
  // preset 'hoy' de ese día.
  const n = _diasEntre(diaDesde, diaHasta);
  return {
    actual: rangoDeDias(diaHasta, n),
    previo: rangoDeDias(sumarDias(diaDesde, -1), n),
  };
}

// 'YYYY-MM-DD' → [00:00, 24:00) en Europe/Madrid, como ISO UTC para el
// filtro gte/lt sobre `inicio`.
export function _rangoDia(dia: string): { desde: string; hasta: string } {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dia)) {
    throw new Error(`_rangoDia: día inválido "${dia}" (esperado YYYY-MM-DD)`);
  }
  const [y, m, d] = partesDia(dia);
  const siguiente = new Date(utcDia(y, m, d + 1));
  const diaSiguiente = siguiente.toISOString().slice(0, 10);
  return {
    desde: new Date(medianocheMadrid(dia)).toISOString(),
    hasta: new Date(medianocheMadrid(diaSiguiente)).toISOString(),
  };
}
